import { inject, isDevMode } from '@angular/core';

import { Logger, LogLevel } from '@ideals/core/logger';
import { PlatformService } from '@ideals/core/platform';
import { IndexedDbService } from '@ideals/services/indexed-db';
import { uniqueId } from '@ideals/utils/unique-id';

import { HttpError, HttpMethod, RequestError } from '../../models';
import { DataTransporterService } from '../data-transporter';
import { isRequestError } from '../errors';

interface BaseLog {
  readonly application: 'app-frontend';
  readonly browser: string;
  readonly clientType: string;
  readonly corporateAccountId: string;
  readonly corporateAccountName: string;
  readonly count: number;
  readonly date: string;
  readonly hostId: string;
  readonly id: string;
  readonly lastOccurred: string;
  readonly os: string;
  readonly roomId: string;
  readonly roomName: string;
  readonly userEmail: string;
  readonly userId: string;
}

interface HttpLog extends BaseLog {
  readonly detail: string;
  readonly errorCode: number;
  readonly errors: unknown;
  readonly headers: string[];
  readonly method: HttpMethod;
  readonly statusCode: number;
  readonly traceparent: string;
  readonly url: string;
}

interface JsLog extends BaseLog {
  readonly exception: string;
  readonly level: LogLevel;
  readonly message: string;
  readonly url: string;
}

type LogData = HttpLog | JsLog;

const CACHE_SIZE = 20;
const ERROR_THROTTLE_INTERVAL = 15000;
const LOGS_LIMIT = 500;
const DUPLICATE_COUNT = 10;

export class LocalLoggerService extends Logger {
  readonly #dataTransporterService = inject(DataTransporterService);
  readonly #indexedDbService = inject(IndexedDbService);
  readonly #platformService = inject(PlatformService);

  readonly #errorCache = new Map<string, number>();

  override available(): boolean {
    return !isDevMode()
      && this.#indexedDbService.available()
      && this.#platformService.platformType() !== 'mobile';
  }

  override saveHttpLog<T>(error: T): void {
    const key = this.#getHttpErrorKey(error as HttpError | RequestError);

    this.#saveLog(key, () => this.#createHttpLog(error as HttpError | RequestError));
  }

  override saveJsLog(error: Error, exception: string, level: LogLevel): void {
    const key = this.#getJsErrorKey(error.message, exception);

    this.#saveLog(key, () => this.#createJsLog(error, exception, level));
  }

  #cleanupErrors(now: number): void {
    const cutoff = now - ERROR_THROTTLE_INTERVAL;

    for (const [key, time] of this.#errorCache.entries()) {
      if (time < cutoff) {
        this.#errorCache.delete(key);
      }
    }
  }

  #createBaseLog(): BaseLog {
    const corporateAccount = this.#dataTransporterService.getCorporateAccount();
    const deviceInfo = this.#platformService.deviceInfo();
    const platformType = this.#platformService.platformType();
    const project = this.#dataTransporterService.getProject();
    const user = this.#dataTransporterService.getUser();

    return {
      application: 'app-frontend',
      browser: `${deviceInfo.browser} ${deviceInfo.browser_version}`,
      clientType: platformType,
      corporateAccountId: corporateAccount?.id ?? '',
      corporateAccountName: corporateAccount?.accountName ?? '',
      count: 1,
      date: new Date().toISOString(),
      hostId: project?.hostId ?? '',
      id: uniqueId(),
      lastOccurred: new Date().toISOString(),
      os: `${deviceInfo.os} ${deviceInfo.os_version}`,
      roomId: project?.id ?? '',
      roomName: project?.name ?? '',
      userEmail: user?.email ?? '',
      userId: user?.id ?? '',
    };
  }

  #createHttpLog(error: HttpError | RequestError): HttpLog {
    const baseLog = this.#createBaseLog();

    return {
      ...baseLog,
      detail: this.#getNormalizedHttpDetail(error),
      errorCode: isRequestError(error) ? error.errorCode : 0,
      errors: this.#getNormalizedHttpErrors(error),
      headers: Array.from(error.headers?.keys?.() ?? {}),
      method: error.method,
      statusCode: error.statusCode,
      traceparent: error.traceparent,
      url: error.url ?? '',
    };
  }

  #createJsLog(error: Error, exception: string, level: LogLevel): JsLog {
    const baseLog = this.#createBaseLog();

    return {
      ...baseLog,
      exception,
      level,
      message: error.message,
      url: window.location.href,
    };
  }

  #getErrorKey(log: LogData): string {
    return 'method' in log
      ? this.#getHttpErrorKey({ method: log.method, statusCode: log.statusCode, url: log.url })
      : this.#getJsErrorKey(log.message, log.exception);
  }

  #getHttpErrorKey(error: Partial<HttpError | RequestError>): string {
    return [
      'http',
      error.method,
      error.url?.split('?')[0] ?? '',
      error.statusCode,
    ].join(':');
  }

  #getJsErrorKey(message: string, exception: string): string {
    return [
      'js',
      message,
      exception,
    ].join(':');
  }

  #getNormalizedHttpDetail(error: HttpError | RequestError): HttpLog['detail'] {
    if (error.status === 'No Response') {
      return navigator.onLine
        ? 'Network/server error: request not completed (possible CORS/Firewall/DNS/Timeout)'
        : 'No internet connection';
    }

    if (isRequestError(error)) {
      return error.detail;
    }

    if ('message' in error) {
      return error['message'] ?? 'Unhandled error';
    }

    return error.status;
  }

  #getNormalizedHttpErrors(error: HttpError | RequestError): HttpLog['errors'] {
    if (isRequestError(error)) {
      return error.errors;
    }

    if ('customErrors' in error) {
      return error['customErrors'];
    }

    if ('errors' in error) {
      return error['errors'];
    }

    if ('error' in error) {
      return error['error'];
    }

    return undefined;
  }

  async #saveLog(errorKey: string, createLogFn: () => LogData): Promise<void> {
    const now = Date.now();
    const lastOccurred = this.#errorCache.get(errorKey);

    if (lastOccurred && now - lastOccurred < ERROR_THROTTLE_INTERVAL) {
      return;
    }

    this.#errorCache.set(errorKey, now);

    const savedLogs = await this.#indexedDbService.getItem<LogData[] | null>('logs') ?? [];
    const newLog = createLogFn();

    const existingIndex = savedLogs.findIndex((log) => {
      return this.#getErrorKey(log) === errorKey && log.count < DUPLICATE_COUNT;
    });

    let updatedLogs: LogData[] = [];

    if (existingIndex >= 0) {
      const existingLog = savedLogs[existingIndex];

      updatedLogs = [...savedLogs];
      updatedLogs[existingIndex] = {
        ...existingLog,
        count: existingLog.count + 1,
        lastOccurred: newLog.date,
      };
    } else {
      updatedLogs = [...savedLogs, newLog];
    }

    const logsToSave = updatedLogs.length > LOGS_LIMIT
      ? updatedLogs.slice(-LOGS_LIMIT)
      : updatedLogs;

    await this.#indexedDbService.setItem<LogData[]>('logs', logsToSave);

    if (this.#errorCache.size > CACHE_SIZE) {
      this.#cleanupErrors(now);
    }
  }
}
